import styles from "./report-an-issue.module.css";

import type { DefaultConfiguration } from "@/types";

import { Group, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";

import { Button, RichTextEditorComponent, Select } from "@/components/inputs";
import { Input, Label } from "@/components/inputs/input";
import { extractUserData } from "@/libs";
import { ISSUE_TYPES, type IssueTypeValue } from "@/libs/issue-types";

interface ReportAnIssueFormProps extends DefaultConfiguration {
  additionalIssueTypeId?: IssueTypeValue;
}

export const ReportAnIssueForm = ({
  apiKey,
  apiURL,
  authStatus,
  issueType,
}: ReportAnIssueFormProps) => {
  const userData = extractUserData(authStatus);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const DEFAULT_TYPE_LABELS = [
    "Payment & Account Issues",
    "IT Support",
    "Feedback",
    "General",
  ];

  const issueTypes = ISSUE_TYPES.filter((type) => {
    return DEFAULT_TYPE_LABELS.includes(type.label) || type.label === issueType;
  });

  useEffect(() => {
    if (issueType && !ISSUE_TYPES.some((type) => type.label === issueType)) {
      console.warn(
        `[ReportAnIssueForm] Invalid additionalIssueType "${issueType}" passed. Check ISSUE_TYPES for valid labels.`
      );
    }
  }, [issueType]);

  const form = useForm({
    initialValues: {
      email: userData?.email ?? "",
      message: "",
      name: userData?.name ?? "",
      subject: "",
      topicId: "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : "Invalid email",
      message: (value) => (!value.trim() ? "Message is required" : null),
      name: (value) => (!value.trim() ? "Name is required" : null),
      subject: (value) => (!value.trim() ? "Subject is required" : null),
      topicId: (value) => (!value ? "Please select an issue type" : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`${apiURL}`, {
        body: JSON.stringify({
          ...values,
        }),
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": `${apiKey}`,
        },
        method: "POST",
      });

      if (response.ok) {
        setSuccess("Your issue has been reported successfully!");
        form.reset();

        setTimeout(() => {
          setSuccess("");
        }, 3000);
      } else {
        throw new Error(`Failed with status code ${response.status}`);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to report the issue. Please try again.");

      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Text fw="bold">Report an Issue</Text>
      <Text fz="xs">
        {userData?.name} Kindly fill the form as correctly as possible so we can
        understand how to help you.
      </Text>

      <form
        className={styles.formGrid}
        onSubmit={form.onSubmit(handleSubmit)}
        autoComplete="off"
      >
        <div className={styles.content}>
          {!userData.name && (
            <Group className="flex-1">
              <Label htmlFor="name" required>
                Name
              </Label>
              <Input
                className={styles.subtitle}
                {...form.getInputProps("name")}
                placeholder="Enter your full name"
              />

              {form.errors.name && (
                <Text c="red" fz="xs">
                  {form.errors.name}
                </Text>
              )}
            </Group>
          )}

          {!userData.email && (
            <Group className="flex-1">
              <Label htmlFor="email" required>
                Email Address
              </Label>
              <Input
                className={styles.subtitle}
                {...form.getInputProps("email")}
                placeholder="Enter your email address"
              />

              {form.errors.email && (
                <Text c="red" fz="xs">
                  {form.errors.email}
                </Text>
              )}
            </Group>
          )}

          <Group>
            <Label htmlFor="subject" required>
              Subject
            </Label>

            <Input
              className={styles.subtitle}
              {...form.getInputProps("subject")}
              placeholder="Enter the subject of your issue"
            />

            {form.errors.subject && (
              <Text c="red" fz="xs">
                {form.errors.subject}
              </Text>
            )}
          </Group>

          <Group>
            <Label htmlFor="topicId" required>
              Issue Type
            </Label>
            <Select
              id="topicId"
              name="topicId"
              onChange={(value) => form.setFieldValue("topicId", value)}
              options={issueTypes || []}
              placeholder="Select an issue type"
              value={form.values.topicId}
            />
            {form.errors.topicId && (
              <Text c="red" fz="xs">
                {form.errors.topicId}
              </Text>
            )}
          </Group>

          <Group>
            <Label htmlFor="message" required>
              Message
            </Label>
            <RichTextEditorComponent
              name="message"
              onChange={(value) => form.setFieldValue("message", value)}
              placeholder="Enter your message"
              value={form.values.message}
            />
            {form.errors.message && (
              <Text c="red" fz="xs">
                {form.errors.message}
              </Text>
            )}
          </Group>

          <div className={styles.actions}>
            <Button
              className={`${styles.button} ${styles.buttonGray}`}
              type="button"
              onClick={() => {
                form.reset();
                setError("");
                setSuccess("");
              }}
            >
              Cancel
            </Button>
            <Button
              className={`${styles.button} ${styles.buttonRed}`}
              type="submit"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </form>
      <div className={styles.feedbackStatus}>
        {error && (
          <Text mb={14} c="red">
            {error}
          </Text>
        )}
        {success && (
          <Text mb={14} c="green">
            {success}
          </Text>
        )}
      </div>
    </div>
  );
};
